import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { LeadSource } from "../types";

export const useLeadSourceMutations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createLeadSource = useMutation({
    mutationFn: async ({ name, description, order }: { name: string; description: string; order: number }) => {
      const { error } = await supabase
        .from('lead_sources')
        .insert([{ name, description, order }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadSources'] });
      toast({
        title: "Success",
        description: "Lead source created successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating lead source:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create lead source",
      });
    },
  });

  const updateLeadSource = useMutation({
    mutationFn: async ({ id, name, description }: { id: string; name: string; description: string }) => {
      const { error } = await supabase
        .from('lead_sources')
        .update({ name, description })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadSources'] });
      toast({
        title: "Success",
        description: "Lead source updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating lead source:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update lead source",
      });
    },
  });

  const deleteLeadSource = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('lead_sources')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadSources'] });
      toast({
        title: "Success",
        description: "Lead source deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Error deleting lead source:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete lead source",
      });
    },
  });

  const updateOrder = useMutation({
    mutationFn: async (leadSources: LeadSource[]) => {
      const updates = leadSources.map((source, index) => ({
        id: source.id,
        name: source.name,
        description: source.description,
        order: index + 1,
      }));

      const { error } = await supabase
        .from('lead_sources')
        .upsert(updates, { onConflict: 'id' });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadSources'] });
      toast({
        title: "Success",
        description: "Lead source order updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating lead source order:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update lead source order",
      });
    },
  });

  return {
    createLeadSource,
    updateLeadSource,
    deleteLeadSource,
    updateOrder,
  };
};